import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import { getCoachTodayApi, snoozeAttentionApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { CW, ErrorBox, Eyebrow, InfoHint, Initials, Loading, PageTitle, Panel, Pill, StatCard, TextLink, UnderlineTabs } from "./cwUi";

/**
 * P3.2 — Coach "Today" (design PDF page 1; phone = page 3).
 * Layout follows the PDF: "Today" header + Plan check-in days / Start next
 * review, 4 cards, "Your review queue" table (Client / Check-in / Coach
 * response / Action, tabs To review · Awaiting client · Completed ·
 * Upcoming, programme filter + sort), Needs attention + Coming up on the right.
 * NEW path /coach/today. Replaces (for the review work) the old
 * Dashboard "Client reviews due" card (/dashboard → ClientReviewDue) and
 * the Trackers → My reviews list (/tracker-reviews). Both old screens stay.
 */

const COLS = "minmax(0,1.35fr) minmax(0,1fr) minmax(0,0.9fr) 130px";

const weekdayOf = (item) => {
  const label = String(item.cadenceLabel || "");
  const parts = label.split("·").map((x) => x.trim()).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1].slice(0, 3) : "";
};

const SORTS = [
  { value: "deadline", label: "Sort: response deadline" },
  { value: "submitted", label: "Sort: submitted first" },
  { value: "name", label: "Sort: client name" },
];

/** One row of the queue table (desktop: 4 columns; phone: a card — design page 3). */
function Row({ name, sub, checkTop, checkBottom, checkTone, response, responseTone, action, onAction, highlight }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: COLS },
        alignItems: "center",
        gap: { xs: 0.75, md: 2 },
        py: { xs: 1.5, md: 1.25 },
        px: { xs: 1.5, md: 2.5 },
        mx: { xs: 0, md: -2.5 },
        my: { xs: 1, md: 0 },
        border: { xs: `1px solid ${CW.line}`, md: 0 },
        borderTop: { md: `1px solid ${CW.line}` },
        borderLeft: { md: highlight ? "3px solid #e8812f" : "3px solid transparent" },
        borderRadius: { xs: "12px", md: 0 },
        bgcolor: highlight ? "#fff7f2" : "#fff",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Initials name={name} size={34} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: CW.ink, fontSize: 15 }} noWrap>
            {name}
          </Typography>
          {sub ? (
            <Typography sx={{ fontSize: 12, color: CW.muted, display: { xs: "none", md: "block" } }} noWrap>
              {sub}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0, display: { xs: "none", md: "block" } }}>
        <Typography sx={{ fontSize: 12.5, color: CW.muted }} noWrap>
          {checkTop}
        </Typography>
        {checkBottom ? (
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: checkTone || CW.muted }} noWrap>
            {checkBottom}
          </Typography>
        ) : null}
      </Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: responseTone || CW.ink, minWidth: 0 }}>
        {response || "—"}
      </Typography>
      <Box>
        {action ? (
          <TextLink onClick={onAction}>
            {action}
            <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
              {" →"}
            </Box>
          </TextLink>
        ) : null}
      </Box>
    </Box>
  );
}

export default function CoachTodayPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [trackerId, setTrackerId] = useState("");
  const [tab, setTab] = useState("review");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sort, setSort] = useState("deadline");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const result = await getCoachTodayApi({ trackerId });
    if (result?.code === 200) setData(result);
    else setError(result?.message || "Could not load Today");
    setLoading(false);
  }, [trackerId]);

  useEffect(() => {
    load();
  }, [load]);

  const queue = useMemo(() => data?.queue || [], [data]);
  const shownQueue = useMemo(() => {
    const list = overdueOnly ? queue.filter((q) => q.overdue) : [...queue];
    if (sort === "submitted") list.sort((a, b) => new Date(a.submittedAt || 0) - new Date(b.submittedAt || 0));
    else if (sort === "name") list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return list;
  }, [queue, overdueOnly, sort]);

  const openReview = (item) => navigate(`/coach/review/${item.submissionId}`);
  // CLIENTS (3 Nov): "Open client" → the new client workspace.
  const openClient = (item) => {
    const key = item.memberId || item.personKey;
    if (key) navigate(`/coach/clients/${encodeURIComponent(key)}`);
  };

  const snooze = async (item) => {
    const result = await snoozeAttentionApi({ trackerId: item.trackerId, personKey: item.personKey, days: 7 });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Snoozed", { variant: "success" });
      load();
    } else enqueueSnackbar(result?.message || "Could not snooze", { variant: "error" });
  };

  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorBox message={error} onRetry={load} />;

  const cards = data?.cards || {};
  const awaiting = data?.awaiting || [];
  const upcoming = data?.upcoming || [];
  const completed = data?.completed || [];
  const nextItem = shownQueue[0] || queue[0];
  const count = queue.length;
  const words = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
  const countWord = count <= 10 ? words[count] : String(count);

  const pickTab = (value) => {
    setTab(value);
    setOverdueOnly(false);
  };

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 1320, mx: "auto", pb: 4 }}>
        {/* Header — design page 1 */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: { xs: "flex-start", md: "flex-end" },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Eyebrow sx={{ fontSize: 12.5 }}>{moment().format("dddd D MMMM")}</Eyebrow>
            <PageTitle>
              Today
              <InfoHint id="today.header" />
            </PageTitle>
            <Typography sx={{ fontSize: { xs: 14, md: 17 }, color: CW.muted, mt: 0.5 }}>
              {count
                ? `${countWord} check-in${count === 1 ? " is" : "s are"} ready for your attention.`
                : "No check-ins are waiting for you right now."}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", md: "flex-end" }, gap: 1 }}>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <button
                type="button"
                className="small-contained-outlined-button"
                style={{ minWidth: 180, padding: "10px 18px" }}
                onClick={() => navigate("/coach/check-ins/planner")}
              >
                Plan check-in days
              </button>
              <button
                type="button"
                className="small-contained-button"
                style={{ minWidth: 200, padding: "10px 18px" }}
                disabled={!nextItem}
                onClick={() => nextItem && openReview(nextItem)}
              >
                Start next review
              </button>
            </Box>
          </Box>
        </Box>

        {/* Stat cards */}
        <InfoHint id="today.cards" sx={{ ml: 0, mb: 1 }} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 2.5,
            mb: 3,
          }}
        >
          <StatCard
            label="Reviews overdue"
            value={cards.overdue || 0}
            hint="Oldest first"
            dot={CW.red}
            valueColor={CW.red}
            active={tab === "review" && overdueOnly}
            onClick={() => {
              setTab("review");
              setOverdueOnly(true);
              setSort("deadline");
            }}
          />
          <StatCard
            label="Ready for action"
            value={cards.ready != null ? cards.ready : Math.max(0, count - (cards.overdue || 0))}
            hint="Work remaining"
            dot={CW.primary}
            valueColor={CW.primary}
            active={tab === "review" && !overdueOnly}
            onClick={() => pickTab("review")}
          />
          <StatCard
            label="Awaiting client"
            value={cards.awaiting || 0}
            hint="Current window open"
            dot={CW.amber}
            valueColor={CW.amber}
            hideOnPhone
            active={tab === "awaiting"}
            onClick={() => pickTab("awaiting")}
          />
          <StatCard
            label="Completed today"
            value={cards.completedToday || 0}
            hint="Review and reply done"
            dot={CW.green}
            valueColor={CW.green}
            hideOnPhone
            active={tab === "completed"}
            onClick={() => pickTab("completed")}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 390px" },
            gap: 3,
            alignItems: "start",
          }}
        >
          {/* Review queue */}
          <Panel sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
              <Typography sx={{ fontSize: { xs: 19, md: 23 }, fontWeight: 700, color: CW.ink }}>
                Your review queue
                <InfoHint id="today.queue" />
              </Typography>
              <Pill tone="teal" sx={{ fontSize: 13, px: 1.75, py: 0.5 }}>
                {count} open
              </Pill>
            </Box>
            <UnderlineTabs
              value={tab}
              onChange={pickTab}
              sx={{ mb: 2 }}
              options={[
                { value: "review", label: "To review" },
                { value: "awaiting", label: "Awaiting client" },
                { value: "completed", label: "Completed" },
                { value: "upcoming", label: "Upcoming" },
              ]}
            />
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  select
                  size="small"
                  value={trackerId}
                  onChange={(e) => setTrackerId(e.target.value)}
                  SelectProps={{ displayEmpty: true }}
                  inputProps={{ "aria-label": "Programme" }}
                  sx={{ minWidth: 240, bgcolor: CW.soft, "& fieldset": { borderColor: "transparent" } }}
                >
                  <MenuItem value="">All</MenuItem>
                  {(data?.filters?.trackers || []).map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </TextField>
                {overdueOnly ? (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setOverdueOnly(false)}
                    sx={{ border: 0, bgcolor: "transparent", p: 0, cursor: "pointer" }}
                    title="Show all"
                  >
                    <Pill tone="red">Overdue only ✕</Pill>
                  </Box>
                ) : null}
              </Box>
              {tab === "review" ? (
                <TextField
                  select
                  size="small"
                  variant="standard"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  inputProps={{ "aria-label": "Sort" }}
                  InputProps={{ disableUnderline: true }}
                  sx={{ "& .MuiSelect-select": { fontSize: 13.5, color: CW.muted } }}
                >
                  {SORTS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns: COLS,
                gap: 2,
                py: 1,
                px: 2.5,
                mx: -2.5,
              }}
            >
              {["Client", "Check-in", "Coach response", "Action"].map((h) => (
                <Eyebrow key={h} sx={{ pl: h === "Client" ? "3px" : 0 }}>
                  {h}
                </Eyebrow>
              ))}
            </Box>

            {tab === "review" &&
              (shownQueue.length === 0 ? (
                <Typography sx={{ color: CW.muted, fontSize: 14, py: 3, textAlign: "center", borderTop: `1px solid ${CW.line}` }}>
                  Nothing to review. Nice work.
                </Typography>
              ) : (
                shownQueue.map((item) => {
                  const feedback = item.state === "feedback_to_send";
                  const day = weekdayOf(item);
                  return (
                    <Row
                      key={item.submissionId}
                      name={item.name}
                      sub={`${item.trackerName}${day ? ` · ${day}` : ""}`}
                      checkTop={item.submittedLabel}
                      checkBottom={item.stateLabel}
                      checkTone={feedback ? CW.primary : CW.muted}
                      response={item.deadlineLabel}
                      responseTone={item.overdue ? CW.red : feedback ? CW.primary : CW.ink}
                      highlight={item.overdue}
                      action={feedback ? "Write feedback" : "Open review"}
                      onAction={() => openReview(item)}
                    />
                  );
                })
              ))}

            {tab === "awaiting" &&
              (awaiting.length === 0 ? (
                <Typography sx={{ color: CW.muted, fontSize: 14, py: 3, textAlign: "center", borderTop: `1px solid ${CW.line}` }}>
                  No open windows waiting.
                </Typography>
              ) : (
                awaiting.slice(0, 60).map((item) => (
                  <Row
                    key={`${item.trackerId}-${item.personKey}-${item.number}`}
                    name={item.name || item.personKey}
                    sub={item.trackerName}
                    checkTop={`Opened ${moment(item.opens).format("ddd D MMM, HH:mm")}`}
                    checkBottom="Not submitted yet"
                    response={item.closes ? `Window closes ${moment(item.closes).format("ddd D MMM, HH:mm")}` : "Window open"}
                    responseTone={CW.muted}
                    action={item.memberId || item.personKey ? "Open client" : ""}
                    onAction={() => openClient(item)}
                  />
                ))
              ))}

            {tab === "completed" &&
              (completed.length === 0 ? (
                <Typography sx={{ color: CW.muted, fontSize: 14, py: 3, textAlign: "center", borderTop: `1px solid ${CW.line}` }}>
                  Nothing completed yet today.
                </Typography>
              ) : (
                completed.map((item) => (
                  <Row
                    key={item.submissionId}
                    name={item.name}
                    sub={item.trackerName}
                    checkTop="Completed today"
                    checkBottom={item.how === "feedback_sent" ? "Feedback sent" : item.how === "excepted" ? "No feedback needed" : "Reviewed"}
                    checkTone={CW.green}
                    response="Done"
                    responseTone={CW.green}
                    action="Open review"
                    onAction={() => openReview(item)}
                  />
                ))
              ))}

            {tab === "upcoming" &&
              (upcoming.length === 0 ? (
                <Typography sx={{ color: CW.muted, fontSize: 14, py: 3, textAlign: "center", borderTop: `1px solid ${CW.line}` }}>
                  No check-ins open in the next 7 days.
                </Typography>
              ) : (
                upcoming.slice(0, 60).map((item) => (
                  <Row
                    key={`${item.trackerId}-${item.personKey}-${item.number}`}
                    name={item.name || item.personKey}
                    sub={item.trackerName}
                    checkTop={`Opens ${moment(item.opens).format("ddd D MMM, HH:mm")}`}
                    checkBottom="Upcoming"
                    response="—"
                    responseTone={CW.muted}
                    action={item.memberId || item.personKey ? "Open client" : ""}
                    onAction={() => openClient(item)}
                  />
                ))
              ))}

            <Typography sx={{ fontSize: 12.5, color: CW.muted, pt: 1.5, borderTop: `1px solid ${CW.line}` }}>
              {tab === "review"
                ? `Showing ${shownQueue.length} review task${shownQueue.length === 1 ? "" : "s"} across your assigned clients`
                : tab === "awaiting"
                  ? `${awaiting.length} client${awaiting.length === 1 ? "" : "s"} with an open check-in window`
                  : tab === "completed"
                    ? `${completed.length} completed today`
                    : `${upcoming.length} check-in${upcoming.length === 1 ? "" : "s"} opening in the next 7 days`}
            </Typography>
          </Panel>

          {/* Right column */}
          <Box sx={{ display: "grid", gap: 3, minWidth: 0 }}>
            <Panel title="Needs attention" info="today.attention" sx={{ p: { xs: 2, md: 3 } }}>
              {(data?.attention || []).length === 0 ? (
                <Typography sx={{ fontSize: 13.5, color: CW.muted }}>No one needs a follow-up.</Typography>
              ) : (
                data.attention.slice(0, 5).map((item, i) => (
                  <Box
                    key={`${item.trackerId}-${item.personKey}`}
                    sx={{ pt: i ? 2 : 0.5, mt: i ? 2 : 0, borderTop: i ? `1px solid ${CW.line}` : "none" }}
                  >
                    <Pill tone="amber" sx={{ fontSize: 13, px: 1.5, py: 0.5 }}>
                      {item.label || "Follow up"}
                    </Pill>
                    <Typography sx={{ fontSize: 17, fontWeight: 700, color: CW.ink, mt: 1.25 }}>
                      {item.name || item.personKey}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: CW.muted, mt: 0.5 }}>{item.detail}</Typography>
                    {item.lastSubmittedAt ? (
                      <Typography sx={{ fontSize: 14, color: CW.muted }}>
                        Last submitted {moment(item.lastSubmittedAt).format("D MMMM")}.
                      </Typography>
                    ) : null}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                      {item.memberId || item.personKey ? <TextLink onClick={() => openClient(item)}>Open client →</TextLink> : <span />}
                      <TextLink onClick={() => snooze(item)} sx={{ color: CW.muted, fontWeight: 600 }} title="Hide for 7 days">
                        Snooze
                      </TextLink>
                    </Box>
                  </Box>
                ))
              )}
            </Panel>

            <Panel title="Coming up" info="today.comingup" sx={{ p: { xs: 2, md: 3 } }}>
              {(data?.comingUp || []).length === 0 ? (
                <Typography sx={{ fontSize: 13.5, color: CW.muted }}>Nothing in the next few days.</Typography>
              ) : (
                data.comingUp.map((item, i) => (
                  <Box key={i} sx={{ py: 1.75, borderTop: i ? `1px solid ${CW.line}` : "none" }}>
                    {item.kind === "call" ? (
                      <>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }}>
                          {moment(item.start).format(moment(item.start).isSame(moment(), "day") ? "HH:mm" : "ddd HH:mm")} ·{" "}
                          {item.title}
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, color: CW.muted, mt: 0.5 }}>
                          {item.minutes ? `${item.minutes} minutes` : "Call"}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }}>
                          {moment(item.date).format("dddd D MMMM")}
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, color: CW.muted, mt: 0.5 }}>{item.title}.</Typography>
                        {item.detail ? <Typography sx={{ fontSize: 13.5, color: CW.muted }}>{item.detail}</Typography> : null}
                      </>
                    )}
                  </Box>
                ))
              )}
            </Panel>
          </Box>
        </Box>
      </Box>
    </div>
  );
}
